import React, { useEffect } from 'react';

interface MicrosoftClarityProps {
  id: string;
}

// Detect react-snap's puppeteer UA so the tag isn't injected into the
// prerendered HTML (it should only run for real, hydrated visitors).
const IS_PRERENDERING =
  typeof navigator !== 'undefined' && navigator.userAgent === 'ReactSnap';

const CLARITY_SRC_PREFIX = 'https://www.clarity.ms/tag/';

/**
 * Loads Microsoft Clarity (session replay + heatmaps) imperatively, deferred
 * off the critical path the same way GoogleTagManager is. No-ops when the
 * project id is empty or during react-snap prerender. The `clarity` command
 * queue is created synchronously so any early calls are preserved until the
 * tag script finishes loading.
 */
export const MicrosoftClarity: React.FC<MicrosoftClarityProps> = ({ id }) => {
  useEffect(() => {
    if (!id || id.trim() === '' || IS_PRERENDERING) {
      return;
    }
    if (typeof window === 'undefined' || typeof document === 'undefined') {
      return;
    }

    const w = window as unknown as {
      clarity?: ((..._args: unknown[]) => void) & { q?: unknown[] };
      requestIdleCallback?: (
        _cb: () => void,
        _opts?: { timeout: number }
      ) => number;
    };

    if (!w.clarity) {
      const queue: unknown[] = [];
      const clarity = ((..._args: unknown[]) => {
        queue.push(_args);
      }) as ((..._args: unknown[]) => void) & { q?: unknown[] };
      clarity.q = queue;
      w.clarity = clarity;
    }

    let injected = false;
    const interactionEvents = [
      'pointerdown',
      'keydown',
      'touchstart',
      'scroll',
    ] as const;

    const cleanupListeners = () => {
      interactionEvents.forEach(evt => {
        window.removeEventListener(evt, inject);
      });
    };

    function inject() {
      if (injected) return;
      injected = true;
      cleanupListeners();

      try {
        if (document.querySelector(`script[src^="${CLARITY_SRC_PREFIX}"]`)) {
          return;
        }
        const script = document.createElement('script');
        script.async = true;
        script.src = `${CLARITY_SRC_PREFIX}${id}`;
        script.onerror = () => {
          console.warn('Clarity script failed to load');
        };
        document.head.appendChild(script);
      } catch (e) {
        console.warn('Clarity initialization failed:', e);
      }
    }

    let idleHandle: number | undefined;
    if (typeof w.requestIdleCallback === 'function') {
      idleHandle = w.requestIdleCallback(inject, { timeout: 3000 });
    }

    interactionEvents.forEach(evt => {
      window.addEventListener(evt, inject, { once: true, passive: true });
    });

    const timeoutId = window.setTimeout(inject, 3000);

    return () => {
      cleanupListeners();
      window.clearTimeout(timeoutId);
      if (
        idleHandle !== undefined &&
        typeof (
          window as unknown as { cancelIdleCallback?: (_h: number) => void }
        ).cancelIdleCallback === 'function'
      ) {
        (
          window as unknown as { cancelIdleCallback: (_h: number) => void }
        ).cancelIdleCallback(idleHandle);
      }
    };
  }, [id]);

  return null;
};

export default MicrosoftClarity;
